import { useId } from 'react';

import { Icon } from '#/components/ui/icons';

import { EquitySectorFacetMenu } from './EquitySectorFacetMenu';
import { ExchangeFacetMenu } from './ExchangeFacetMenu';
import { FUND_CATEGORY_OPTIONS } from './facetOptions';
import { MarketCapFacetMenu } from './MarketCapFacetMenu';
import { MultiSelectFacetMenu } from './MultiSelectFacetMenu';
import {
  FACET_POPUP_WIDTH_CLASS_NAMES,
  FACET_TRIGGER_WIDTH_CLASS_NAMES,
} from './searchFacetFilterPrimitives';
import type {
  SearchFacetChange,
  SearchFacetFiltersProps,
} from './searchFacetFilters.types';
import {
  ALL_EXCHANGE_CODES,
  ALL_MARKET_CAP_CATEGORIES,
  EMPTY_STRING_VALUES,
  getToggledFacetValues,
} from './searchFacetFilterUtils';

export type { SearchFacetChange, SearchFacetFiltersProps };

export function SearchFacetFilters({
  investmentType,
  equitySectorSlugs,
  equityIndustrySlugs,
  marketCapCategories,
  fundCategorySlugs,
  listingType,
  exchangeCodes,
  onChange,
}: SearchFacetFiltersProps) {
  const id = useId();
  const selectedEquitySectorSlugs = equitySectorSlugs ?? EMPTY_STRING_VALUES;
  const selectedEquityIndustrySlugs =
    equityIndustrySlugs ?? EMPTY_STRING_VALUES;
  const selectedMarketCapCategories =
    marketCapCategories ?? ALL_MARKET_CAP_CATEGORIES;
  const selectedFundCategorySlugs = fundCategorySlugs ?? EMPTY_STRING_VALUES;
  const selectedExchangeCodes = exchangeCodes ?? ALL_EXCHANGE_CODES;
  const showEquitySector = investmentType === 'equity';
  const showMarketCapCategory = investmentType === 'equity';
  const showFundCategory =
    investmentType === 'etf' || investmentType === 'mutual_fund';
  const showListingType =
    investmentType === 'equity' || investmentType === 'etf';
  const hasActiveFilters =
    selectedEquitySectorSlugs.length > 0 ||
    selectedEquityIndustrySlugs.length > 0 ||
    selectedMarketCapCategories.length !== ALL_MARKET_CAP_CATEGORIES.length ||
    selectedFundCategorySlugs.length > 0 ||
    (listingType != null && listingType !== 'all') ||
    selectedExchangeCodes.length !== ALL_EXCHANGE_CODES.length;

  if (!investmentType) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-12" aria-labelledby={id}>
      <p id={id} className="sr-only">
        Refine results
      </p>

      {showEquitySector ? (
        <EquitySectorFacetMenu
          selectedSectorSlugs={selectedEquitySectorSlugs}
          selectedIndustrySlugs={selectedEquityIndustrySlugs}
          triggerClassName={FACET_TRIGGER_WIDTH_CLASS_NAMES.sector}
          popupClassName={FACET_POPUP_WIDTH_CLASS_NAMES.sector}
          popupMatchTrigger={false}
          onApply={(next) => {
            onChange({
              equity_sector_slug: next.equity_sector_slug,
              equity_industry_slug: next.equity_industry_slug,
            });
          }}
        />
      ) : null}

      {showMarketCapCategory ? (
        <MarketCapFacetMenu
          selectedMarketCapCategories={selectedMarketCapCategories}
          triggerClassName={FACET_TRIGGER_WIDTH_CLASS_NAMES.marketCap}
          popupClassName={FACET_POPUP_WIDTH_CLASS_NAMES.marketCap}
          popupMatchTrigger={false}
          onApply={(nextMarketCapCategories) => {
            onChange({
              market_cap_categories: nextMarketCapCategories,
            });
          }}
        />
      ) : null}

      {showFundCategory ? (
        <MultiSelectFacetMenu
          label="Category"
          selectedValues={selectedFundCategorySlugs}
          options={FUND_CATEGORY_OPTIONS}
          triggerClassName={FACET_TRIGGER_WIDTH_CLASS_NAMES.category}
          popupClassName={FACET_POPUP_WIDTH_CLASS_NAMES.category}
          popupMatchTrigger={false}
          onToggle={(value) => {
            const nextFundCategorySlugs = getToggledFacetValues(
              selectedFundCategorySlugs,
              value,
              FUND_CATEGORY_OPTIONS,
            );

            onChange({
              fund_category_slug: nextFundCategorySlugs.length
                ? nextFundCategorySlugs
                : undefined,
            });
          }}
        />
      ) : null}

      {showListingType ? (
        <ExchangeFacetMenu
          selectedExchangeCodes={selectedExchangeCodes}
          triggerClassName={FACET_TRIGGER_WIDTH_CLASS_NAMES.exchange}
          popupClassName={FACET_POPUP_WIDTH_CLASS_NAMES.exchange}
          popupMatchTrigger={false}
          onApply={(nextExchangeCodes) => {
            onChange({
              listing_type: undefined,
              exchange: nextExchangeCodes,
            });
          }}
        />
      ) : null}

      {hasActiveFilters ? (
        <button
          type="button"
          className="mdc-btn mdc-btn--link text-static-14"
          title="Reset filters"
          onClick={() => {
            onChange({
              equity_sector_slug: undefined,
              equity_industry_slug: undefined,
              market_cap_categories: undefined,
              fund_category_slug: undefined,
              listing_type: undefined,
              exchange: undefined,
            } satisfies SearchFacetChange);
          }}
        >
          <Icon id="close" size={16} aria-hidden="true" />
          <span className="sm:sr-only">Reset filters</span>
        </button>
      ) : null}
    </div>
  );
}
